"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NationDetails = exports.DefaultNationData = exports.NationalLevel = void 0;
exports.getNationData = getNationData;
exports.calculateNationalBonus = calculateNationalBonus;
const nations_1 = require("./nations");
const playerTraits_1 = require("./playerTraits");
var NationalLevel;
(function (NationalLevel) {
    NationalLevel["BASIC"] = "BASIC";
    NationalLevel["HERO"] = "HERO";
    NationalLevel["ICON"] = "ICON";
})(NationalLevel || (exports.NationalLevel = NationalLevel = {}));
exports.DefaultNationData = {
    favoredOpponent: [nations_1.Nations.KOREA_NORTH],
    nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
    bonusStat: { block: 1 },
};
exports.NationDetails = {
    // UK & Ireland Region
    /**
     * @param {number} tackle - Reflecting a football culture that historically celebrates and emphasizes strong, committed tackling.
     * @param {PlayerTraits.LONG_BALL} nationalTrait - The national trait.
     * @param {Nations.GERMANY} favoredOpponent - This is one of world football's most significant rivalries. England is granted favoured status based on iconic victories, chiefly the 1966 World Cup Final and the 5-1 away win in 2002.
     * @param {Nations.WALES} favoredOpponent - England has a long-standing statistical dominance over Wales.
     */
    [nations_1.Nations.ENGLAND]: {
        favoredOpponent: [nations_1.Nations.GERMANY, nations_1.Nations.WALES],
        nationalTrait: playerTraits_1.PlayerTraits.LONG_BALL,
        bonusStat: { tackle: 1 },
    },
    /**
     * @param {number} tackle - Scotland has a history of producing tough, physically committed players.
     * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
     * @param {Nations.SPAIN} favoredOpponent - Scotland has secured famous victories against strong Spanish sides, notably in 1986 World Cup qualifiers and Euro 2024 qualifiers.
     * @param {Nations.ENGLAND} favoredOpponent - Justified by historic underdog victories like the "Wembley Wizards" in 1928 and defeating the world champions in 1967.
     */
    [nations_1.Nations.SCOTLAND]: {
        favoredOpponent: [nations_1.Nations.ENGLAND, nations_1.Nations.SPAIN],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { tackle: 1 },
    },
    /**
     * @param {number} run - Reflecting a legacy of fast, dynamic wingers like Gareth Bale and Ryan Giggs.
     * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
     * @param {Nations.BELGIUM} favoredOpponent - Based on the iconic 3-1 win in the Euro 2016 quarter-finals.
     * @param {Nations.SCOTLAND} favoredOpponent - Wales has enjoyed a better head-to-head record over the last four decades.
     */
    [nations_1.Nations.WALES]: {
        favoredOpponent: [nations_1.Nations.BELGIUM, nations_1.Nations.SCOTLAND],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} tackle - Reflecting a tradition of tenacious, hard-working players.
     * @param {PlayerTraits.LUCKY} nationalTrait - The national trait.
     * @param {Nations.ITALY} favoredOpponent - Based on iconic victories at the 1994 World Cup and Euro 2016.
     * @param {Nations.ENGLAND} favoredOpponent - A fierce rivalry where Ireland often performs well against expectations.
     */
    [nations_1.Nations.IRELAND]: {
        favoredOpponent: [nations_1.Nations.ITALY, nations_1.Nations.ENGLAND],
        nationalTrait: playerTraits_1.PlayerTraits.LUCKY,
        bonusStat: { tackle: 1 },
    },
    /**
     * @param {number} block - Representing a legacy of outstanding goalkeepers like Pat Jennings.
     * @param {PlayerTraits.UNYIELDING} nationalTrait - The national trait.
     * @param {Nations.SPAIN} favoredOpponent - Based on the famous 1-0 victory against hosts Spain at the 1982 World Cup.
     * @param {Nations.WALES} favoredOpponent - Northern Ireland has often proven to be a difficult opponent for Wales, securing significant results against expectations.
     */
    [nations_1.Nations.NORTHERN_IRELAND]: {
        favoredOpponent: [nations_1.Nations.SPAIN, nations_1.Nations.WALES],
        nationalTrait: playerTraits_1.PlayerTraits.UNYIELDING,
        bonusStat: { block: 1 },
    },
    // Western & Central Europe Region
    /**
     * @param {number} shoot - Reflecting Germany's history of powerful finishers and efficient, clinical goalscoring.
     * @param {PlayerTraits.RELIABLE} nationalTrait - The national trait.
     * @param {Nations.BRAZIL} favoredOpponent - Based on the stunning 7-1 victory over hosts Brazil in the 2014 World Cup semi-final.
     * @param {Nations.FRANCE} favoredOpponent - Germany holds iconic World Cup semi-final victories in '82 and '86, plus a crucial quarter-final win in 2014.
     */
    [nations_1.Nations.GERMANY]: {
        favoredOpponent: [nations_1.Nations.BRAZIL, nations_1.Nations.FRANCE],
        nationalTrait: playerTraits_1.PlayerTraits.RELIABLE,
        bonusStat: { shoot: 1 },
    },
    /**
     * @param {number} run - Reflecting France's production of dynamic players with pace, dribbling ability, and flair.
     * @param {PlayerTraits.DOMINANT} nationalTrait - The national trait.
     * @param {Nations.BRAZIL} favoredOpponent - France defeated Brazil in the 1998 World Cup Final and the 2006 World Cup Quarter-Final.
     * @param {Nations.BELGIUM} favoredOpponent - Based on the victory in the crucial 2018 World Cup semi-final.
     */
    [nations_1.Nations.FRANCE]: {
        favoredOpponent: [nations_1.Nations.BRAZIL, nations_1.Nations.BELGIUM],
        nationalTrait: playerTraits_1.PlayerTraits.DOMINANT,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} pass - Symbolising the Dutch philosophy of "Total Football".
     * @param {PlayerTraits.PLAYMAKER} nationalTrait - The national trait.
     * @param {Nations.ARGENTINA} favoredOpponent - A storied rivalry where the Netherlands have historically performed well.
     * @param {Nations.GERMANY} favoredOpponent - Based on the hugely significant victory over West Germany in the Euro '88 semi-final.
     */
    [nations_1.Nations.NETHERLANDS]: {
        favoredOpponent: [nations_1.Nations.ARGENTINA, nations_1.Nations.GERMANY],
        nationalTrait: playerTraits_1.PlayerTraits.PLAYMAKER,
        bonusStat: { pass: 1 },
    },
    /**
     * @param {number} run - Reflecting Belgium's 'golden generation' of technically gifted, fast, and skillful dribblers.
     * @param {PlayerTraits.PLAYMAKER} nationalTrait - The national trait.
     * @param {Nations.BRAZIL} favoredOpponent - Reflecting their victory in the 2018 World Cup quarter-final.
     * @param {Nations.NETHERLANDS} favoredOpponent - Based on the intensity of the "Low Countries Derby" and Belgium's higher peak ranking during their recent 'golden generation'.
     */
    [nations_1.Nations.BELGIUM]: {
        favoredOpponent: [nations_1.Nations.NETHERLANDS, nations_1.Nations.BRAZIL],
        nationalTrait: playerTraits_1.PlayerTraits.PLAYMAKER,
        bonusStat: { run: 1 },
    }, /**
     * @param {number} shoot - Bonus stat.
     * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
     * @param {Nations.NETHERLANDS} favoredOpponent - A key rival.
     * @param {Nations.GERMANY} favoredOpponent - A key rival.
     */
    [nations_1.Nations.CZECH_REPUBLIC]: {
        favoredOpponent: [nations_1.Nations.NETHERLANDS, nations_1.Nations.GERMANY],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { shoot: 1 },
    },
    /**
     * @param {number} tackle - Bonus stat.
     * @param {PlayerTraits.POWERFUL} nationalTrait - The national trait.
     * @param {Nations.PORTUGAL} favoredOpponent - A key rival.
     * @param {Nations.GERMANY} favoredOpponent - A key rival.
     */
    [nations_1.Nations.POLAND]: {
        favoredOpponent: [nations_1.Nations.PORTUGAL, nations_1.Nations.GERMANY],
        nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
        bonusStat: { tackle: 1 },
    },
    /**
     * @param {number} pass - Bonus stat.
     * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
     * @param {Nations.GERMANY} favoredOpponent - A key rival.
     * @param {Nations.SWITZERLAND} favoredOpponent - A key rival.
     */
    [nations_1.Nations.AUSTRIA]: {
        favoredOpponent: [nations_1.Nations.GERMANY, nations_1.Nations.SWITZERLAND],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { pass: 1 },
    },
    /**
     * @param {number} pass - Bonus stat.
     * @param {PlayerTraits.UNYIELDING} nationalTrait - The national trait.
     * @param {Nations.FRANCE} favoredOpponent - A key rival.
     * @param {Nations.AUSTRIA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.SWITZERLAND]: {
        favoredOpponent: [nations_1.Nations.FRANCE, nations_1.Nations.AUSTRIA],
        nationalTrait: playerTraits_1.PlayerTraits.UNYIELDING,
        bonusStat: { pass: 1 },
    },
    // Northern Europe / Scandinavia Region
    /**
     * @param {number} tackle - Often associated with organised defence, physical presence, and disciplined team play.
     * @param {PlayerTraits.ORGANISED} nationalTrait - The national trait.
     * @param {Nations.ENGLAND} favoredOpponent - Sweden has often been a bogey team for the English in a historical rivalry.
     * @param {Nations.DENMARK} favoredOpponent - Sweden has greater historical consistency and more World Cup knockout appearances.
     */
    [nations_1.Nations.SWEDEN]: {
        favoredOpponent: [nations_1.Nations.ENGLAND, nations_1.Nations.DENMARK],
        nationalTrait: playerTraits_1.PlayerTraits.ORGANISED,
        bonusStat: { tackle: 1 },
    },
    /**
     * @param {number} pass - Reflecting Denmark's periods of stylish, technical football, such as the 'Danish Dynamite' era.
     * @param {PlayerTraits.INSPIRE} nationalTrait - The national trait.
     * @param {Nations.NETHERLANDS} favoredOpponent - Denmark beat the reigning champions, the Netherlands, in the Euro 1992 semi-final.
     * @param {Nations.NORWAY} favoredOpponent - Denmark's Euro '92 triumph gives them the single most significant achievement among the Nordic countries.
     */
    [nations_1.Nations.DENMARK]: {
        favoredOpponent: [nations_1.Nations.NETHERLANDS, nations_1.Nations.NORWAY],
        nationalTrait: playerTraits_1.PlayerTraits.INSPIRE,
        bonusStat: { pass: 1 },
    },
    /**
     * @param {number} shoot - Representing powerful strikers like Solskjær, Flo, and Haaland.
     * @param {PlayerTraits.GOAL_MACHINE} nationalTrait - The national trait.
     * @param {Nations.BRAZIL} favoredOpponent - Norway holds a unique unbeaten historical record against Brazil, including a famous victory in the 1998 World Cup.
     * @param {Nations.FINLAND} favoredOpponent - Historically, Norway has been the more established footballing nation with more major tournament appearances.
     */
    [nations_1.Nations.NORWAY]: {
        favoredOpponent: [nations_1.Nations.BRAZIL, nations_1.Nations.FINLAND],
        nationalTrait: playerTraits_1.PlayerTraits.GOAL_MACHINE,
        bonusStat: { shoot: 1 },
    },
    /**
     * @param {number} intercept - Reflecting a team often built on solid defensive organisation and tactical discipline.
     * @param {PlayerTraits.DETERMINED} nationalTrait - The national trait.
     * @param {Nations.DENMARK} favoredOpponent - Stemming from Finland's victory over them in the emotional opening game of Euro 2020.
     * @param {Nations.SWEDEN} favoredOpponent - Represents the classic underdog dynamic against their larger, historically dominant neighbour.
     */
    [nations_1.Nations.FINLAND]: {
        favoredOpponent: [nations_1.Nations.DENMARK, nations_1.Nations.SWEDEN],
        nationalTrait: playerTraits_1.PlayerTraits.DETERMINED,
        bonusStat: { intercept: 1 },
    },
    // Eastern Europe Region
    /**
     * @param {number} tackle - Bonus stat.
     * @param {PlayerTraits.DOMINANT} nationalTrait - The national trait.
     * @param {Nations.SPAIN} favoredOpponent - A key rival.
     * @param {Nations.CROATIA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.RUSSIA]: {
        favoredOpponent: [nations_1.Nations.SPAIN, nations_1.Nations.CROATIA],
        nationalTrait: playerTraits_1.PlayerTraits.DOMINANT,
        bonusStat: { tackle: 1 },
    },
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.UNYIELDING} nationalTrait - The national trait.
     * @param {Nations.SWEDEN} favoredOpponent - A key rival.
     * @param {Nations.SERBIA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.UKRAINE]: {
        favoredOpponent: [nations_1.Nations.SWEDEN, nations_1.Nations.SERBIA],
        nationalTrait: playerTraits_1.PlayerTraits.UNYIELDING,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} block - Bonus stat.
     * @param {PlayerTraits.POWERFUL} nationalTrait - The national trait.
     * @param {Nations.GERMANY} favoredOpponent - A key rival.
     * @param {Nations.RUSSIA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.SERBIA]: {
        favoredOpponent: [nations_1.Nations.GERMANY, nations_1.Nations.RUSSIA],
        nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
        bonusStat: { block: 1 },
    },
    /**
     * @param {number} pass - Bonus stat.
     * @param {PlayerTraits.FLAIR} nationalTrait - The national trait.
     * @param {Nations.ENGLAND} favoredOpponent - A key rival.
     * @param {Nations.UKRAINE} favoredOpponent - A key rival.
     */
    [nations_1.Nations.CROATIA]: {
        favoredOpponent: [nations_1.Nations.ENGLAND, nations_1.Nations.UKRAINE],
        nationalTrait: playerTraits_1.PlayerTraits.FLAIR,
        bonusStat: { pass: 1 },
    },
    /**
     * @param {number} shoot - Bonus stat.
     * @param {PlayerTraits.IGNITE} nationalTrait - The national trait.
     * @param {Nations.NETHERLANDS} favoredOpponent - A key rival.
     * @param {Nations.CROATIA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.TURKEY]: {
        favoredOpponent: [nations_1.Nations.NETHERLANDS, nations_1.Nations.CROATIA],
        nationalTrait: playerTraits_1.PlayerTraits.IGNITE,
        bonusStat: { shoot: 1 },
    },
    // Africa Region
    /**
   * @param {number} pass - Bonus stat.
   * @param {PlayerTraits.PLAYMAKER} nationalTrait - The national trait.
   * @param {Nations.ITALY} favoredOpponent - A key rival.
   * @param {Nations.MOROCCO} favoredOpponent - A key rival.
   */
    [nations_1.Nations.EGYPT]: {
        favoredOpponent: [nations_1.Nations.ITALY, nations_1.Nations.MOROCCO],
        nationalTrait: playerTraits_1.PlayerTraits.PLAYMAKER,
        bonusStat: { pass: 1 },
    },
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.POWERFUL} nationalTrait - The national trait.
     * @param {Nations.ARGENTINA} favoredOpponent - Nigeria defeated Argentina in the 1996 Olympic Final.
     * @param {Nations.EGYPT} favoredOpponent - Nigeria has often had a strong head-to-head record against them in tournaments.
     */
    [nations_1.Nations.NIGERIA]: {
        favoredOpponent: [nations_1.Nations.ARGENTINA, nations_1.Nations.EGYPT],
        nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
        bonusStat: { run: 1 },
    },
    /**
   * @param {number} run - Fitting Senegal's modern teams known for athleticism, pace, and power.
   * @param {PlayerTraits.RAPID} nationalTrait - The national trait.
   * @param {Nations.FRANCE} favoredOpponent - Based on the famous 1-0 victory over the reigning world champions France in the opening match of the 2002 World Cup.
   * @param {Nations.NIGERIA} favoredOpponent - As recent Africa Cup of Nations champions (2022), they hold current bragging rights.
   */
    [nations_1.Nations.SENEGAL]: {
        favoredOpponent: [nations_1.Nations.FRANCE, nations_1.Nations.NIGERIA],
        nationalTrait: playerTraits_1.PlayerTraits.RAPID,
        bonusStat: { run: 1 },
    },
    /**
   * @param {number} run - Reflecting the technical skill, dribbling ability, and pace often found in their players.
   * @param {PlayerTraits.FLAIR} nationalTrait - The national trait.
   * @param {Nations.BELGIUM} favoredOpponent - Based on the impressive 2-0 victory over Belgium in the 2022 World Cup group stage.
   * @param {Nations.ALGERIA} favoredOpponent - A major North African rivalry.
   */
    [nations_1.Nations.MOROCCO]: {
        favoredOpponent: [nations_1.Nations.BELGIUM, nations_1.Nations.ALGERIA],
        nationalTrait: playerTraits_1.PlayerTraits.FLAIR,
        bonusStat: { run: 1 },
    },
    /**
   * @param {number} run - Bonus stat.
   * @param {PlayerTraits.POWERFUL} nationalTrait - The national trait.
   * @param {Nations.JAPAN} favoredOpponent - Cote d'Ivoire defeated Japan 2-1 in the group stage of the 2014 World Cup.
   * @param {Nations.GHANA} favoredOpponent - Ivory Coast won the Africa Cup of Nations in 2015, defeating Ghana in the final.
   */
    [nations_1.Nations.IVORY_COAST]: {
        favoredOpponent: [nations_1.Nations.JAPAN, nations_1.Nations.GHANA],
        nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
        bonusStat: { run: 1 },
    },
    /**
   * @param {number} tackle - Bonus stat.
   * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
   * @param {Nations.ALGERIA} favoredOpponent - A key rival.
   * @param {Nations.TUNISIA} favoredOpponent - A key rival.
   */
    [nations_1.Nations.BURKINA_FASO]: {
        favoredOpponent: [nations_1.Nations.ALGERIA, nations_1.Nations.TUNISIA],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { tackle: 1 },
    },
    /**
   * @param {number} shoot - Bonus stat.
   * @param {PlayerTraits.UNYIELDING} nationalTrait - The national trait.
   * @param {Nations.ARGENTINA} favoredOpponent - A key rival.
   * @param {Nations.NIGERIA} favoredOpponent - A key rival.
   */
    [nations_1.Nations.CAMEROON]: {
        favoredOpponent: [nations_1.Nations.ARGENTINA, nations_1.Nations.NIGERIA],
        nationalTrait: playerTraits_1.PlayerTraits.UNYIELDING,
        bonusStat: { shoot: 1 },
    },
    /**
   * @param {number} run - Often relying on pace and athleticism to compete.
   * @param {PlayerTraits.LUCKY} nationalTrait - The national trait.
   * @param {Nations.GHANA} favoredOpponent - Zimbabwe secured a notable 2-1 victory over Ghana in the 2006 Africa Cup of Nations.
   * @param {Nations.ZAMBIA} favoredOpponent - A significant Southern African rivalry known as the "Battle of the Zambezi."
   */
    [nations_1.Nations.ZIMBABWE]: {
        favoredOpponent: [nations_1.Nations.GHANA, nations_1.Nations.ZAMBIA],
        nationalTrait: playerTraits_1.PlayerTraits.LUCKY,
        bonusStat: { run: 1 },
    },
    /**
   * @param {number} intercept - Bonus stat.
   * @param {PlayerTraits.ORGANISED} nationalTrait - The national trait.
   * @param {Nations.FRANCE} favoredOpponent - A key rival.
   * @param {Nations.ALGERIA} favoredOpponent - A key rival.
   */
    [nations_1.Nations.TUNISIA]: {
        favoredOpponent: [nations_1.Nations.FRANCE, nations_1.Nations.ALGERIA],
        nationalTrait: playerTraits_1.PlayerTraits.ORGANISED,
        bonusStat: { intercept: 1 },
    },
    /**
   * @param {number} run - Bonus stat.
   * @param {PlayerTraits.POWERFUL} nationalTrait - The national trait.
   * @param {Nations.URUGUAY} favoredOpponent - A key rival.
   * @param {Nations.IVORY_COAST} favoredOpponent - A key rival.
   */
    [nations_1.Nations.GHANA]: {
        favoredOpponent: [nations_1.Nations.URUGUAY, nations_1.Nations.IVORY_COAST],
        nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
        bonusStat: { run: 1 },
    },
    // Asia Region
    /**
   * @param {number} pass - Bonus stat.
   * @param {PlayerTraits.ORGANISED} nationalTrait - The national trait.
   * @param {Nations.GERMANY} favoredOpponent - A key rival.
   * @param {Nations.KOREA_SOUTH} favoredOpponent - A key rival.
   */
    [nations_1.Nations.JAPAN]: {
        favoredOpponent: [nations_1.Nations.GERMANY, nations_1.Nations.KOREA_SOUTH],
        nationalTrait: playerTraits_1.PlayerTraits.ORGANISED,
        bonusStat: { pass: 1 },
    },
    /**
   * @param {number} run - Bonus stat.
   * @param {PlayerTraits.DETERMINED} nationalTrait - The national trait.
   * @param {Nations.PORTUGAL} favoredOpponent - A key rival.
   * @param {Nations.QATAR} favoredOpponent - A key rival.
   */
    [nations_1.Nations.KOREA_SOUTH]: {
        favoredOpponent: [nations_1.Nations.PORTUGAL, nations_1.Nations.QATAR],
        nationalTrait: playerTraits_1.PlayerTraits.DETERMINED,
        bonusStat: { run: 1 },
    },
    /**
   * @param {number} tackle - Bonus stat.
   * @param {PlayerTraits.POWERFUL} nationalTrait - The national trait.
   * @param {Nations.ARGENTINA} favoredOpponent - A key rival.
   * @param {Nations.JAPAN} favoredOpponent - A key rival.
   */
    [nations_1.Nations.SAUDI_ARABIA]: {
        favoredOpponent: [nations_1.Nations.ARGENTINA, nations_1.Nations.JAPAN],
        nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
        bonusStat: { tackle: 1 },
    },
    /**
   * @param {number} pass - Bonus stat.
   * @param {PlayerTraits.PLAYMAKER} nationalTrait - The national trait.
   * @param {Nations.SWITZERLAND} favoredOpponent - A key rival.
   * @param {Nations.SAUDI_ARABIA} favoredOpponent - A key rival.
   */
    [nations_1.Nations.QATAR]: {
        favoredOpponent: [nations_1.Nations.SWITZERLAND, nations_1.Nations.SAUDI_ARABIA],
        nationalTrait: playerTraits_1.PlayerTraits.PLAYMAKER,
        bonusStat: { pass: 1 },
    },
    /**
   * @param {number} tackle - Often associated with a robust, physical approach.
   * @param {PlayerTraits.DOMINANT} nationalTrait - The national trait.
   * @param {Nations.FRANCE} favoredOpponent - China achieved a rare victory against France 1-0 in a friendly before the 2010 World Cup.
   * @param {Nations.KOREA_SOUTH} favoredOpponent - China's 1-0 victory in a 2018 World Cup qualifier was their first-ever competitive win against them.
   */
    [nations_1.Nations.CHINA]: {
        favoredOpponent: [nations_1.Nations.FRANCE, nations_1.Nations.KOREA_SOUTH],
        nationalTrait: playerTraits_1.PlayerTraits.DOMINANT,
        bonusStat: { tackle: 1 },
    },
    /**
   * @param {number} pass - Reflecting the style of key creative players.
   * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
   * @param {Nations.QATAR} favoredOpponent - India's 0-0 draw away to the reigning Asian champions during 2022 World Cup qualifying was a monumental result.
   * @param {Nations.BANGLADESH} favoredOpponent - A key South Asian rivalry where India has historically been the dominant team.
   */
    [nations_1.Nations.INDIA]: {
        favoredOpponent: [nations_1.Nations.QATAR, nations_1.Nations.BANGLADESH],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { pass: 1 },
    }, // North America Region
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.FLAIR} nationalTrait - The national trait.
     * @param {Nations.BRAZIL} favoredOpponent - A key rival.
     * @param {Nations.CANADA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.MEXICO]: {
        favoredOpponent: [nations_1.Nations.BRAZIL, nations_1.Nations.CANADA],
        nationalTrait: playerTraits_1.PlayerTraits.FLAIR,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.POWERFUL} nationalTrait - The national trait.
     * @param {Nations.ENGLAND} favoredOpponent - A key rival.
     * @param {Nations.MEXICO} favoredOpponent - A key rival.
     */
    [nations_1.Nations.USA]: {
        favoredOpponent: [nations_1.Nations.ENGLAND, nations_1.Nations.MEXICO],
        nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.RAPID} nationalTrait - The national trait.
     * @param {Nations.USA} favoredOpponent - A key rival.
     * @param {Nations.COSTA_RICA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.CANADA]: {
        favoredOpponent: [nations_1.Nations.USA, nations_1.Nations.COSTA_RICA],
        nationalTrait: playerTraits_1.PlayerTraits.RAPID,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} intercept - Bonus stat.
     * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
     * @param {Nations.ITALY} favoredOpponent - A key rival.
     * @param {Nations.USA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.COSTA_RICA]: {
        favoredOpponent: [nations_1.Nations.ITALY, nations_1.Nations.USA],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { intercept: 1 },
    },
    // Caribbean Region
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.RAPID} nationalTrait - The national trait.
     * @param {Nations.MEXICO} favoredOpponent - A key rival.
     * @param {Nations.TRINIDAD_AND_TOBAGO} favoredOpponent - A key rival.
     */
    [nations_1.Nations.JAMAICA]: {
        favoredOpponent: [nations_1.Nations.MEXICO, nations_1.Nations.TRINIDAD_AND_TOBAGO],
        nationalTrait: playerTraits_1.PlayerTraits.RAPID,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.FLAIR} nationalTrait - The national trait.
     * @param {Nations.USA} favoredOpponent - T&T famously defeated the USA 2-1 in the final match of 2018 World Cup qualifying, eliminating the US from the tournament.
     * @param {Nations.HAITI} favoredOpponent - Trinidad and Tobago's qualification for the 2006 World Cup stands as the most significant recent achievement between these two nations.
     */
    [nations_1.Nations.TRINIDAD_AND_TOBAGO]: {
        favoredOpponent: [nations_1.Nations.USA, nations_1.Nations.HAITI],
        nationalTrait: playerTraits_1.PlayerTraits.FLAIR,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} run - Often characterized by energetic, athletic players with good stamina and pace.
     * @param {PlayerTraits.DETERMINED} nationalTrait - The national trait.
     * @param {Nations.ITALY} favoredOpponent - Haiti famously took the lead against Italy in the 1974 World Cup, ending Dino Zoff's record-breaking run of minutes without conceding a goal.
     * @param {Nations.CUBA} favoredOpponent - Haiti has performed more strongly and consistently in recent CONCACAF competitions compared to Cuba.
     */
    [nations_1.Nations.HAITI]: {
        favoredOpponent: [nations_1.Nations.ITALY, nations_1.Nations.CUBA],
        nationalTrait: playerTraits_1.PlayerTraits.DETERMINED,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} tackle - Often employing a robust and physical approach, particularly in defence.
     * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
     * @param {Nations.USA} favoredOpponent - The intense political history and the significance of any competitive result for Cuba against their vastly more resourced neighbour provide justification.
     * @param {Nations.JAMAICA} favoredOpponent - Relies on the potential for major upsets within regional derbies and Cuba's stronger standing in the distant past.
     */
    [nations_1.Nations.CUBA]: {
        favoredOpponent: [nations_1.Nations.USA, nations_1.Nations.JAMAICA],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { tackle: 1 },
    },
    // South America Region
    /**
     * @param {number} run - Representing a history of incredibly skillful dribblers like Maradona and Messi.
     * @param {PlayerTraits.CHARGE} nationalTrait - The national trait.
     * @param {Nations.ENGLAND} favoredOpponent - An intense rivalry marked by controversial moments and significant Argentine victories in World Cups ('86, '98).
     * @param {Nations.BRAZIL} favoredOpponent - Argentina holds the most recent major honours, defeating Brazil in the 2021 Copa América final.
     */
    [nations_1.Nations.ARGENTINA]: {
        favoredOpponent: [nations_1.Nations.ENGLAND, nations_1.Nations.BRAZIL],
        nationalTrait: playerTraits_1.PlayerTraits.CHARGE,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} run - Synonymous with skillful dribbling, flair, and attacking prowess ('Joga Bonito').
     * @param {PlayerTraits.STAR} nationalTrait - The national trait.
     * @param {Nations.ITALY} favoredOpponent - Brazil has faced Italy in two World Cup finals, winning in 1970 and on penalties in 1994.
     * @param {Nations.COLOMBIA} favoredOpponent - Brazil's unparalleled five World Cup titles give them historical dominance.
     */
    [nations_1.Nations.BRAZIL]: {
        favoredOpponent: [nations_1.Nations.ITALY, nations_1.Nations.COLOMBIA],
        nationalTrait: playerTraits_1.PlayerTraits.STAR,
        bonusStat: { run: 1 },
    },
    /**
    * @param {number} tackle - Reflecting the high intensity, pressing style, and aggressive tackling characteristic of Chile's successful generation.
    * @param {PlayerTraits.DETERMINED} nationalTrait - The national trait.
    * @param {Nations.SPAIN} favoredOpponent - Chile eliminated defending champions Spain from the 2014 World Cup with a decisive 2-0 group stage victory.
    * @param {Nations.ARGENTINA} favoredOpponent - Chile achieved their greatest triumphs by defeating Argentina in two consecutive Copa América finals (2015 and 2016).
    */
    [nations_1.Nations.CHILE]: {
        favoredOpponent: [nations_1.Nations.SPAIN, nations_1.Nations.ARGENTINA],
        nationalTrait: playerTraits_1.PlayerTraits.DETERMINED,
        bonusStat: { tackle: 1 },
    },
    /**
    * @param {number} tackle - Often associated with a strong, physical midfield presence complementing their attacking flair.
    * @param {PlayerTraits.FLAIR} nationalTrait - The national trait.
    * @param {Nations.POLAND} favoredOpponent - Based on the convincing 3-0 victory over Poland in the 2018 World Cup.
    * @param {Nations.CHILE} favoredOpponent - Colombia has arguably been the more consistent nation over the last 10-15 years.
    */
    [nations_1.Nations.COLOMBIA]: {
        favoredOpponent: [nations_1.Nations.POLAND, nations_1.Nations.CHILE],
        nationalTrait: playerTraits_1.PlayerTraits.FLAIR,
        bonusStat: { tackle: 1 },
    },
    /**
    * @param {number} tackle - Reflecting a strong, competitive spirit.
    * @param {PlayerTraits.DETERMINED} nationalTrait - The national trait.
    * @param {Nations.BRAZIL} favoredOpponent - Based on the iconic "Maracanazo" victory in the 1950 World Cup final.
    * @param {Nations.ARGENTINA} favoredOpponent - A classic, intense South American rivalry.
    */
    [nations_1.Nations.URUGUAY]: {
        favoredOpponent: [nations_1.Nations.BRAZIL, nations_1.Nations.ARGENTINA],
        nationalTrait: playerTraits_1.PlayerTraits.DETERMINED,
        bonusStat: { tackle: 1 },
    },
    // Oceania Region
    /**
     * @param {number} tackle - Reflecting the physical, competitive style associated with Australian sport.
     * @param {PlayerTraits.POWERFUL} nationalTrait - The national trait.
     * @param {Nations.URUGUAY} favoredOpponent - Australia famously defeated Uruguay in a penalty shootout in the 2006 World Cup play-off.
     * @param {Nations.NEW_ZEALAND} favoredOpponent - Historically, Australia was the dominant force in Oceania.
     */
    [nations_1.Nations.AUSTRALIA]: {
        favoredOpponent: [nations_1.Nations.URUGUAY, nations_1.Nations.NEW_ZEALAND],
        nationalTrait: playerTraits_1.PlayerTraits.POWERFUL,
        bonusStat: { tackle: 1 },
    },
    /**
     * @param {number} intercept - Often relying on defensive organisation and discipline to compete.
     * @param {PlayerTraits.UNDERDOG} nationalTrait - The national trait.
     * @param {Nations.ITALY} favoredOpponent - New Zealand secured a famous 1-1 draw against the reigning world champions Italy at the 2010 World Cup.
     * @param {Nations.FIJI} favoredOpponent - As the dominant nation in Oceania following Australia's departure, New Zealand consistently wins regional competitions.
     */
    [nations_1.Nations.NEW_ZEALAND]: {
        favoredOpponent: [nations_1.Nations.ITALY, nations_1.Nations.FIJI],
        nationalTrait: playerTraits_1.PlayerTraits.UNDERDOG,
        bonusStat: { intercept: 1 },
    },
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.RAPID} nationalTrait - The national trait.
     * @param {Nations.NEW_ZEALAND} favoredOpponent - A key rival.
     * @param {Nations.SOLOMON_ISLANDS} favoredOpponent - A key rival.
     */
    [nations_1.Nations.FIJI]: {
        favoredOpponent: [nations_1.Nations.NEW_ZEALAND, nations_1.Nations.SOLOMON_ISLANDS],
        nationalTrait: playerTraits_1.PlayerTraits.RAPID,
        bonusStat: { run: 1 },
    },
    /**
     * @param {number} run - Bonus stat.
     * @param {PlayerTraits.LUCKY} nationalTrait - The national trait.
     * @param {Nations.FIJI} favoredOpponent - A key rival.
     * @param {Nations.AUSTRALIA} favoredOpponent - A key rival.
     */
    [nations_1.Nations.SOLOMON_ISLANDS]: {
        favoredOpponent: [nations_1.Nations.FIJI, nations_1.Nations.AUSTRALIA],
        nationalTrait: playerTraits_1.PlayerTraits.LUCKY,
        bonusStat: { run: 1 },
    },
    // Southern Europe Region (Consolidated)
    /**
     * @param {number} intercept - Symbolising a tradition of tactical discipline and defensive organisation, famously demonstrated during their Euro 2004 triumph.
     * @param {PlayerTraits.UNYIELDING} nationalTrait - The national trait.
     * @param {Nations.FRANCE} favoredOpponent - Greece's victory over reigning champions France in the Euro 2004 quarter-finals was a pivotal moment.
     * @param {Nations.PORTUGAL} favoredOpponent - Defeating the host nation Portugal in the Euro 2004 final is the most iconic moment in Greek football history.
     */
    [nations_1.Nations.GREECE]: {
        favoredOpponent: [nations_1.Nations.FRANCE, nations_1.Nations.PORTUGAL],
        nationalTrait: playerTraits_1.PlayerTraits.UNYIELDING,
        bonusStat: { intercept: 1 },
    },
    /**
     * @param {number} shoot - Representing a nation famed for prolific goalscorers like Eusébio and Cristiano Ronaldo.
     * @param {PlayerTraits.FLAIR} nationalTrait - The national trait.
     * @param {Nations.NETHERLANDS} favoredOpponent - Portugal defeated them in high-stakes matches at Euro 2004, the 2006 World Cup, and Euro 2012.
     * @param {Nations.SPAIN} favoredOpponent - Portugal's major trophy wins at Euro 2016 and the 2019 Nations League elevated their status in the Iberian derby.
     */
    [nations_1.Nations.PORTUGAL]: {
        favoredOpponent: [nations_1.Nations.NETHERLANDS, nations_1.Nations.SPAIN],
        nationalTrait: playerTraits_1.PlayerTraits.FLAIR,
        bonusStat: { shoot: 1 },
    },
    /**
     * @param {number} intercept - Symbolising a strong tradition of tactical intelligence and defensive organisation.
     * @param {PlayerTraits.ORGANISED} nationalTrait - The national trait.
     * @param {Nations.FRANCE} favoredOpponent - Italy defeated France in the 2006 World Cup Final, providing a monumental justification.
     * @param {Nations.GREECE} favoredOpponent - As a historical footballing superpower, Italy holds a clear position of dominance over Greece.
     */
    [nations_1.Nations.ITALY]: {
        favoredOpponent: [nations_1.Nations.FRANCE, nations_1.Nations.GREECE],
        nationalTrait: playerTraits_1.PlayerTraits.ORGANISED,
        bonusStat: { intercept: 1 },
    },
    /**
     * @param {number} pass - Reflecting the nation's modern identity built on technical, possession-based football known as "tiki-taka".
     * @param {PlayerTraits.TIKI_TAKA} nationalTrait - The national trait.
     * @param {Nations.NETHERLANDS} favoredOpponent - Spain's victory over the Netherlands in the 2010 World Cup Final is a monumental achievement.
     * @param {Nations.ITALY} favoredOpponent - Spain's decisive 4-0 victory over Italy in the Euro 2012 Final provides a strong justification.
     */
    [nations_1.Nations.SPAIN]: {
        favoredOpponent: [nations_1.Nations.NETHERLANDS, nations_1.Nations.ITALY],
        nationalTrait: playerTraits_1.PlayerTraits.TIKI_TAKA,
        bonusStat: { pass: 1 },
    },
};
// Function remains the same as user provided
function getNationData(nation) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return exports.NationDetails[nation] || exports.DefaultNationData; // Added non-null assertion as keys should exist
}
// Updated to use NationalLevel enum
function calculateNationalBonus(nation, nationalLevel = NationalLevel.BASIC) {
    const nationData = getNationData(nation);
    const bonus = {
        statBlock: nationData.bonusStat,
        positionsEnabled: [],
        playerTraits: [],
        preferredOpponent: nationData.favoredOpponent,
    };
    if (nationalLevel === NationalLevel.HERO ||
        nationalLevel === NationalLevel.ICON) {
        bonus.playerTraits.push(nationData.nationalTrait);
    }
    if (nationalLevel === NationalLevel.ICON) {
        bonus.playerTraits.push(playerTraits_1.PlayerTraits.ICON);
    }
    return bonus;
}
